import { useMemo } from 'react';
import useSWR from 'swr';
import { axiosInstance } from '@/api/api';
import { useAppState } from '@/app/appContext';
import { useCurrentUser } from '@/hooks/useCurrentUser';

/** Группы филиалов, доступные человеку (Алексей 22.10, #1969) — BE GET /core/api/branch-groups/mine/. */
export interface MyBranchGroup {
  id: number;
  name: string;
  zone_ids: number[];
}

export const MY_BRANCH_GROUPS_KEY = 'branch-groups:mine';

export function useMyBranchGroups() {
  return useSWR<MyBranchGroup[]>(
    MY_BRANCH_GROUPS_KEY,
    async () => (await axiosInstance.get<MyBranchGroup[]>('/core/api/branch-groups/mine/')).data,
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );
}

/** Режим групп в списке филиалов: группы показываем, если их у человека ≥ 2 (или он из Линзы);
 *  «Все филиалы» прячем, если групп ≥ 2 — смотреть вместе разные группы нет смысла. */
export function useBranchGroupMode() {
  const { data: groups } = useMyBranchGroups();
  const { user } = useCurrentUser();
  const isSuperuser = user?.is_superuser === true;
  const list = groups ?? [];
  return {
    groups: list,
    // Группы видны всем, у кого они есть, — и при одной группе (Алексей 25.10 #2317: у JonySpb
    // висела «Все филиалы», а вида по группам не было). «Все филиалы» — суперпользователю и
    // тем, кому не выдано ни одной группы.
    showGroups: list.length > 0,
    showAllBranches: isSuperuser || list.length === 0,
  };
}

/** Какие филиалы показывать в режиме «Все филиалы»: null — все доступные, иначе только
 *  филиалы выбранной группы. Применяют портфельные экраны (дашборд, отчёт, статус, калибровка). */
export function useBranchScope(): { groupId: string | null; groupName: string | null; zoneIds: Set<string> | null } {
  const { zone } = useAppState();
  const { data: groups } = useMyBranchGroups();
  return useMemo(() => {
    if (!zone.branchGroupId) return { groupId: null, groupName: null, zoneIds: null };
    const g = (groups ?? []).find(x => String(x.id) === zone.branchGroupId);
    return {
      groupId: zone.branchGroupId,
      groupName: g?.name ?? null,
      // Группа ещё грузится — пустой набор, а не «все»: не мигать чужими филиалами.
      zoneIds: new Set((g?.zone_ids ?? []).map(String)),
    };
  }, [zone.branchGroupId, groups]);
}
